import { useRequest } from 'features/request/useRequest';
import { WorkOrderStatusEnum } from 'gql/graphql';
import { useSetIsWorkOrderChangeRequired } from 'pages/Project/BimProject/hooks/useSetIsWorkOrderChangeRequired';

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { bimRemoveWorkOrderOtherForActGql, bimRemoveWorkOrderOthersGql } from '../bimWorkOrderOthersQueries';

interface IUseBimWorkOrderDeletePrices {
    workOrderId: number;
    checkedIds: number[];
    refreshTable: () => void;
    resetCheckedIds: () => void;
    statusWorkOrder: WorkOrderStatusEnum;
    workOrderObjectId?: number;
}

const requestId = 'deleteOthersFromBimWorkOrder';

export const useBimWorkOrderDeletePrices = (props: IUseBimWorkOrderDeletePrices) => {
    const dispatch = useDispatch();

    const { workOrderId, checkedIds, refreshTable, resetCheckedIds, statusWorkOrder, workOrderObjectId } = props;

    const onSuccess = useCallback(() => {
        refreshTable();
        resetCheckedIds();
    }, [refreshTable, resetCheckedIds]);

    const [
        bimRemoveWorkOrderOthersData,
        bimRemoveWorkOrderOthersStatus,
        bimRemoveWorkOrderOthersError,
        bimRemoveWorkOrderOthersPricesRequest,
        bimRemoveWorkOrderOthersSetData,
    ] = useRequest({
        requestId,
        withEffect: false,
        query: bimRemoveWorkOrderOthersGql,
        dependenciesUpdater: onSuccess,
    });

    const [
        bimRemoveWorkOrderOtherForActData,
        bimRemoveWorkOrderOtherForActStatus,
        bimRemoveWorkOrderOtherForActError,
        bimRemoveWorkOrderOtherForActRequest,
        bimRemoveWorkOrderOtherForActSetData,
    ] = useRequest({
        requestId,
        withEffect: false,
        query: bimRemoveWorkOrderOtherForActGql,
        dependenciesUpdater: onSuccess,
    });

    useSetIsWorkOrderChangeRequired({
        useData: bimRemoveWorkOrderOtherForActData?.bimRemoveWorkOrderOtherForAct?.data,
        status: bimRemoveWorkOrderOtherForActStatus,
    });

    const isInProgress = useMemo(() => statusWorkOrder === WorkOrderStatusEnum.InProgress, [statusWorkOrder]);

    const handleDelete = useCallback(() => {
        if (isInProgress) {
            dispatch(bimRemoveWorkOrderOtherForActRequest({ workOrderObjectId, workOrderOtherIds: checkedIds }));
        } else {
            dispatch(bimRemoveWorkOrderOthersPricesRequest({ workOrderId, workOrderOtherIds: checkedIds }));
        }
    }, [
        bimRemoveWorkOrderOtherForActRequest,
        bimRemoveWorkOrderOthersPricesRequest,
        checkedIds,
        dispatch,
        isInProgress,
        workOrderId,
        workOrderObjectId,
    ]);

    const deleteData = useMemo(
        () =>
            isInProgress
                ? bimRemoveWorkOrderOtherForActData?.bimRemoveWorkOrderOtherForAct
                : bimRemoveWorkOrderOthersData?.bimRemoveWorkOrderOthers,
        [
            bimRemoveWorkOrderOtherForActData?.bimRemoveWorkOrderOtherForAct,
            bimRemoveWorkOrderOthersData?.bimRemoveWorkOrderOthers,
            isInProgress,
        ],
    );

    const deleteStatus = useMemo(
        () => (isInProgress ? bimRemoveWorkOrderOtherForActStatus : bimRemoveWorkOrderOthersStatus),
        [bimRemoveWorkOrderOtherForActStatus, bimRemoveWorkOrderOthersStatus, isInProgress],
    );

    const deleteError = useMemo(
        () => (isInProgress ? bimRemoveWorkOrderOtherForActError : bimRemoveWorkOrderOthersError),
        [bimRemoveWorkOrderOtherForActError, bimRemoveWorkOrderOthersError, isInProgress],
    );

    const deleteSetData = useMemo(
        () => (isInProgress ? bimRemoveWorkOrderOtherForActSetData : bimRemoveWorkOrderOthersSetData),
        [bimRemoveWorkOrderOtherForActSetData, bimRemoveWorkOrderOthersSetData, isInProgress],
    );

    return {
        deleteData,
        deleteStatus,
        deleteError,
        deleteSetData,
        handleDelete,
        onSuccess,
    };
};
